import {
  AdminApiError,
  isDailyRedisWindow,
  type AdminClient,
  type RedisCapacity,
  type RedisHistory,
  type RedisHistoryWindow,
} from '@apx/admin-sdk';
import { fmtBytes, fmtDate, fmtDateTime, fmtDuration, fmtNumber, fmtTime } from '../format.js';
import { lineChart } from '../line-chart.js';
import { bindPager, renderPager } from '../pager.js';
import {
  HISTORY_WINDOWS,
  advisories,
  barFillClass,
  dayStateLabel,
  growthLabel,
  limitSubLabel,
  outageSummary,
  pctLabel,
  statusTone,
  toneToPillClass,
  windowFromSearch,
  type Advisory,
} from './redis-status.js';

/**
 * Redis monitoring — superadmin only (#448).
 *
 * Sibling of system-health.ts, but note the two deliberate divergences from
 * that page: per-section error handling (a history failure must not blank the
 * health card) and a working interval teardown (see the cleanup comment at the
 * bottom — system-health's observer never actually fires).
 */

export interface RedisMonitoringPageDeps {
  client: AdminClient;
}

const HEALTH_REFRESH_MS = 30_000;
/** The sampler writes a point a minute, and rebuilding ~300 SVG nodes every
 *  30s flickers for nothing. */
const HISTORY_REFRESH_MS = 300_000;

export async function renderRedisMonitoringPage(
  root: HTMLElement,
  deps: RedisMonitoringPageDeps,
): Promise<void> {
  let windowKey: RedisHistoryWindow = windowFromSearch(location.search);
  let stopped = false;
  // Declared up here, not at its assignment below, because handleRestricted()
  // clears it — and that runs during the FIRST load, before the interval is
  // started. As a `const` at the assignment site it was a temporal-dead-zone
  // ReferenceError, swallowed by the enclosing Promise.allSettled, so a
  // non-superadmin got a permanent "Loading…" instead of the Restricted card.
  let timer: number | undefined;
  let healthInFlight = false;
  let historyInFlight = false;
  let lastHistoryAt = 0;
  // Samples-table paging. Held here so a background refresh repaints the table
  // without knocking the reader back to page 1, and so changing page re-renders
  // from the last payload instead of refetching.
  let lastHistory: RedisHistory | null = null;
  let samplePage = 1;
  let samplePageSize = 10;

  // Shell is written ONCE; only the #rm-* bodies repaint afterwards. That is
  // what lets the window-switcher listeners bind a single time.
  root.innerHTML = `
    <h1 class="page-title">Redis monitoring</h1>
    <p class="page-sub">
      Memory, capacity headroom and dataset growth for the shared Redis instance — the same
      instance that backs the BullMQ queues, the socket adapter and the config cache.
      Superadmin only. Health refreshes every ${HEALTH_REFRESH_MS / 1000}s, the chart every
      ${HISTORY_REFRESH_MS / 60_000} min.
      <br />
      1h/24h/7d are per-minute samples kept for 8 days; 30d/90d/1y are one rolled-up row per
      day, which is what makes a year of history affordable to store.
    </p>
    <div id="rm-alerts"></div>
    <div id="rm-health"><p class="muted">Loading…</p></div>
    <div id="rm-capacity"></div>
    <div class="card">
      <div class="row" style="justify-content:space-between;align-items:center;margin-top:0">
        <h2 style="margin:0">Memory history</h2>
        <div class="seg" id="rm-window" role="tablist" aria-label="History window">
          ${HISTORY_WINDOWS
            .map(
              (w) =>
                `<button type="button" class="seg-btn${w === windowKey ? ' active' : ''}" data-window="${w}" role="tab" aria-selected="${w === windowKey}">${w}</button>`,
            )
            .join('')}
        </div>
      </div>
      <div id="rm-chart"><p class="muted">Loading…</p></div>
    </div>
    <div id="rm-namespaces"></div>
    <div id="rm-history"></div>
  `;

  const el = (id: string): HTMLElement => root.querySelector<HTMLElement>(`#${id}`)!;
  const alertsEl = el('rm-alerts');
  const healthEl = el('rm-health');
  const capacityEl = el('rm-capacity');
  const chartEl = el('rm-chart');
  const namespacesEl = el('rm-namespaces');
  const historyEl = el('rm-history');
  const windowEl = el('rm-window');

  /** A 403 here is a real answer, not a transient error: stop polling it. */
  function handleRestricted(): void {
    stopped = true;
    window.clearInterval(timer);
    alertsEl.innerHTML = '';
    capacityEl.innerHTML = '';
    chartEl.innerHTML = '';
    namespacesEl.innerHTML = '';
    historyEl.innerHTML = '';
    healthEl.innerHTML = `
      <div class="card">
        <h2>Restricted</h2>
        <p>Redis monitoring is limited to super administrators, and your account does not have access.</p>
        <p class="muted">
          <a href="/system-health">System health</a> shows Redis reachability and latency and is
          open to anyone with <code>audit.read</code>.
        </p>
      </div>`;
  }

  function describe(err: unknown): string {
    if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
    return err instanceof Error ? err.message : String(err);
  }

  /** Route an error to one section only, so other sections keep last-good. */
  function sectionError(target: HTMLElement, err: unknown): void {
    if (err instanceof AdminApiError && err.status === 403) {
      handleRestricted();
      return;
    }
    target.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
  }

  async function loadHealth(): Promise<void> {
    if (stopped || healthInFlight) return;
    healthInFlight = true;
    try {
      const h = await deps.client.fetchRedisCapacity();
      alertsEl.innerHTML = renderAlerts(advisories(h));
      healthEl.innerHTML = renderHealth(h);
      capacityEl.innerHTML = renderCapacity(h);
      namespacesEl.innerHTML = renderNamespaces(h);
    } catch (err) {
      sectionError(healthEl, err);
    } finally {
      healthInFlight = false;
    }
  }

  /** Repaint the samples table from the cached payload and re-bind the pager
   *  (innerHTML replacement drops the old listeners every time). */
  function paintSamples(): void {
    if (!lastHistory) return;
    const total = lastHistory.points.length;
    const pageCount = Math.max(1, Math.ceil(total / samplePageSize));
    // A refresh can shrink the series (e.g. after switching window); clamp
    // rather than stranding the reader on a page that no longer exists.
    samplePage = Math.min(samplePage, pageCount);
    historyEl.innerHTML = renderHistoryTable(lastHistory, samplePage, samplePageSize);
    bindPager(
      historyEl,
      (p) => {
        samplePage = p;
        paintSamples();
      },
      (size) => {
        samplePageSize = size;
        samplePage = 1;
        paintSamples();
      },
    );
  }

  async function loadHistory(): Promise<void> {
    if (stopped || historyInFlight) return;
    historyInFlight = true;
    try {
      const h = await deps.client.fetchRedisHistory(windowKey);
      lastHistoryAt = Date.now();
      lastHistory = h;
      chartEl.innerHTML = renderChart(h);
      paintSamples();
    } catch (err) {
      sectionError(chartEl, err);
    } finally {
      historyInFlight = false;
    }
  }

  windowEl.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.window as RedisHistoryWindow;
      if (next === windowKey) return;
      windowKey = next;
      windowEl.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((b) => {
        const on = b.dataset.window === windowKey;
        b.classList.toggle('active', on);
        b.setAttribute('aria-selected', String(on));
      });
      // replaceState, NOT navigate(): navigate() dispatches route:change, which
      // re-runs routeChanged(), blanks els.content and tears this page down for
      // what is only page-local sub-state. Same precedent as game-detail.ts and
      // integrator-detail.ts. Back/forward still work — popstate re-runs the
      // route and the page re-reads windowFromSearch on mount.
      history.replaceState({}, '', `${location.pathname}?window=${windowKey}`);
      // New window means a different series — start the table at the top.
      samplePage = 1;
      chartEl.innerHTML = '<p class="muted">Loading…</p>';
      void loadHistory();
    });
  });

  await Promise.allSettled([loadHealth(), loadHistory()]);

  timer = window.setInterval(() => {
    if (stopped) return;
    // A backgrounded tab has nobody looking at it; same guard as system-health.
    if (document.visibilityState !== 'visible') return;
    void loadHealth();
    if (Date.now() - lastHistoryAt >= HISTORY_REFRESH_MS) void loadHistory();
  }, HEALTH_REFRESH_MS);

  // Teardown. Deliberately NOT system-health.ts's pattern: that one observes
  // document.body for `root` being detached, but `root` is els.content and the
  // router only replaces its CHILDREN (main.ts sets els.content.innerHTML), so
  // the element is never removed and the observer never fires — it leaks an
  // interval per visit. Watch root's own childList for our sentinel instead
  // (the home.ts / jackpot.ts pattern). No `subtree`: this page repaints its
  // own section bodies, which are grandchildren, so it cannot self-trigger.
  const cleanup = new MutationObserver(() => {
    if (!root.querySelector('#rm-health')) {
      stopped = true;
      window.clearInterval(timer);
      cleanup.disconnect();
    }
  });
  cleanup.observe(root, { childList: true });
}

// ───────────────────────────────────────────── Section renderers

function renderAlerts(items: Advisory[]): string {
  if (items.length === 0) return '';
  // Issue #448 lists "Capacity Warning" last; it renders FIRST here. An alert
  // sitting under three cards and a chart is an alert nobody reads.
  return items
    .map(
      (a) => `
    <div class="card rm-alert rm-alert--${a.level}">
      <strong>${escapeHtml(a.title)}</strong>
      <p class="muted" style="margin:6px 0 0">${escapeHtml(a.body)}</p>
    </div>`,
    )
    .join('');
}

function renderHealth(h: RedisCapacity): string {
  const down = h.connection === 'down';
  const pill = down
    ? '<span class="pill warn">down</span>'
    : `<span class="${toneToPillClass(statusTone(h.status))}">${escapeHtml(h.status)}</span>`;
  const rowClass = down ? 'sh-row sh-row--down' : 'sh-row';
  const row = (label: string, value: string): string =>
    `<div class="${rowClass}"><span>${escapeHtml(label)}</span><span class="num">${value}</span></div>`;

  if (down) {
    return `
      <div class="card">
        <div class="row" style="justify-content:space-between;align-items:center;margin-top:0">
          <h2 style="margin:0">Redis ${pill}</h2>
          <span class="muted">checked ${escapeHtml(fmtTime(h.checkedAt))}</span>
        </div>
        <p class="error">${escapeHtml(h.error ?? 'Redis is unreachable.')}</p>
      </div>`;
  }

  const p = h.persistence!;
  const m = h.memory!;
  // Six lines that answer "is it healthy"; the diagnostic counters the issue
  // also asks for sit behind the disclosure below, so the card opens calm.
  return `
    <div class="card">
      <div class="row" style="justify-content:space-between;align-items:center;margin-top:0">
        <h2 style="margin:0">Redis health ${pill}</h2>
        <span class="muted">checked ${escapeHtml(fmtTime(h.checkedAt))}</span>
      </div>
      <div class="sh-grid">
        <div class="sh-col"><div>
          ${row('Uptime', escapeHtml(fmtDuration(h.server!.uptimeSeconds * 1000)))}
          ${row('Connected clients', fmtNumber(h.clients!.connected))}
          ${row('Eviction policy', `<code>${escapeHtml(m.policy)}</code>`)}
        </div></div>
        <div class="sh-col"><div>
          ${row('AOF', p.aofEnabled ? (p.aofLastWriteOk ? 'on · writes ok' : 'on · WRITES FAILING') : 'off')}
          ${row('RDB', p.rdbLastBgsaveOk ? 'last save ok' : 'LAST SAVE FAILED')}
          ${row('Unsaved writes', fmtNumber(p.changesSinceLastSave))}
        </div></div>
      </div>
      <details class="rm-details">
        <summary>Diagnostics</summary>
        <div class="sh-grid">
          <div class="sh-col"><div>
            ${row('Redis version', escapeHtml(h.server!.version))}
            ${row('Total keys', fmtNumber(h.keyspace!.keyCount))}
            ${row('Blocked clients', fmtNumber(h.clients!.blocked))}
          </div></div>
          <div class="sh-col"><div>
            ${row('Evicted keys', fmtNumber(h.keyspace!.evictedKeys))}
            ${row('Expired keys', fmtNumber(h.keyspace!.expiredKeys))}
            ${row('Fragmentation', m.fragmentationRatio === null ? '—' : m.fragmentationRatio.toFixed(2))}
          </div></div>
        </div>
      </details>
    </div>`;
}

function renderCapacity(h: RedisCapacity): string {
  if (h.connection === 'down' || !h.memory) {
    return '<div class="card"><p class="muted">Unavailable while Redis is unreachable.</p></div>';
  }
  const m = h.memory;
  const tile = (label: string, value: string, sub = '', cls = ''): string => `
    <div class="stat-card ${cls}">
      <div class="stat-card-label">${escapeHtml(label)}</div>
      <div class="stat-card-value">${value}</div>
      ${sub ? `<div class="stat-card-sub">${sub}</div>` : ''}
    </div>`;
  // Secondary readings the issue still requires, kept as a quiet strip rather
  // than five more competing tiles.
  const meta = (pairs: Array<[string, string]>): string =>
    `<div class="rm-meta">${pairs
      .map(([k, v]) => `<span><em>${escapeHtml(k)}</em>${v}</span>`)
      .join('')}</div>`;

  // The bar carries tick marks at the warn/critical thresholds, so the reading
  // is "where am I relative to the alarm" rather than an abstract fraction.
  const bar =
    m.usedPct === null
      ? ''
      : `<div class="rm-bar" role="progressbar" aria-valuenow="${m.usedPct}" aria-valuemin="0" aria-valuemax="100">
           <div class="${barFillClass(h.status)}" style="width:${Math.min(100, m.usedPct)}%"></div>
           <span class="rm-bar-mark" style="left:${h.thresholds.warnPct}%" title="warning ${h.thresholds.warnPct}%"></span>
           <span class="rm-bar-mark" style="left:${h.thresholds.criticalPct}%" title="critical ${h.thresholds.criticalPct}%"></span>
         </div>`;

  return `
    <div class="card">
      <h2>Memory &amp; capacity</h2>
      <div class="players-stats rm-stats-3">
        ${tile('Usage', pctLabel(m.usedPct), escapeHtml(limitSubLabel(h)), 'is-primary')}
        ${tile('Memory used', escapeHtml(fmtBytes(m.usedBytes)), `dataset ${escapeHtml(fmtBytes(m.datasetBytes))}`)}
        ${tile('Headroom', escapeHtml(m.freeBytes === null ? 'n/a' : fmtBytes(m.freeBytes)), m.freeBytes === null ? '<span class="muted">no limit configured</span>' : 'before writes fail')}
      </div>
      ${bar}
      ${meta([
        ['Peak', escapeHtml(fmtBytes(m.peakBytes))],
        ['RSS', escapeHtml(fmtBytes(m.rssBytes))],
        [
          'Container',
          h.containerUsedPct === null
            ? '<span class="muted">not configured</span>'
            : `${pctLabel(h.containerUsedPct)} of ${escapeHtml(fmtBytes(h.containerLimitBytes))}`,
        ],
        ['Keys', fmtNumber(h.keyspace!.keyCount)],
      ])}
    </div>`;
}

function renderChart(h: RedisHistory): string {
  const pts = h.points.map((p) => ({ x: Date.parse(p.t), y: p.usedMaxBytes }));
  // Days Redis was unreachable are absent from `points` on purpose — plotting
  // them as zero would draw a memory cliff that never happened — so they get
  // said out loud instead, or they would be invisible.
  const outage = outageSummary(h.outages);
  const outageNote = outage === null ? '' : `<p class="muted rm-note">${escapeHtml(outage)}</p>`;
  const limit = h.points.at(-1)?.maxMemoryBytes ?? null;
  const formatX = (x: number): string => (h.window === '1h' || h.window === '24h' ? fmtTime(x) : fmtDate(x));
  const cadence = fmtDuration(h.bucketSeconds * 1000);

  // One series only. A second chart for key count was more surface than signal
  // — the count already appears in the capacity strip, the namespace table and
  // the samples table, and bytes-vs-count share no meaningful scale.
  const chart = lineChart({
    series: [{ label: 'Memory used', points: pts, className: 'lc-line--mem', area: true }],
    refLines: limit === null ? [] : [{ y: limit, label: `limit ${fmtBytes(limit)}` }],
    formatY: fmtBytes,
    formatX,
    tooltip: (x, [mem]) => `${fmtDateTime(x)} · ${fmtBytes(mem ?? 0)}`,
    tickMode: 'binary',
    height: 170,
    yTickCount: 3,
    emptyMessage: `No samples yet — the sampler writes a point every ${cadence}.`,
    singlePointMessage: 'One sample so far — a trend line needs at least two.',
    ariaLabel: `Redis memory usage over the last ${h.window}`,
  });

  return chart + outageNote;
}

function renderNamespaces(h: RedisCapacity): string {
  if (h.connection === 'down') return '';
  if (!h.namespaces || h.namespaces.length === 0) {
    return `<div class="card"><h2>Keys by namespace</h2>
      <p class="muted">No namespace scan recorded yet — the sampler runs it on a slower cadence than the memory sample.</p></div>`;
  }
  const total = h.namespaces.reduce((a, b) => a + b.count, 0) || 1;
  const TOP = 6;
  const top = h.namespaces.slice(0, TOP);
  const rest = h.namespaces.slice(TOP);
  const line = (label: string, count: number, code: boolean): string => `<tr>
        <td>${code ? `<code>${escapeHtml(label)}:*</code>` : `<span class="muted">${escapeHtml(label)}</span>`}</td>
        <td class="num">${fmtNumber(count)}</td>
        <td class="num">${((count / total) * 100).toFixed(1)}%</td>
      </tr>`;
  // Long tails of one-off prefixes are noise; roll them into a single line so
  // the table stays six rows plus a remainder.
  const rows =
    top.map((ns) => line(ns.prefix, ns.count, true)).join('') +
    (rest.length > 0
      ? line(
          `${rest.length} smaller namespace${rest.length === 1 ? '' : 's'}`,
          rest.reduce((a, b) => a + b.count, 0),
          false,
        )
      : '');
  return `
    <div class="card">
      <h2>Keys by namespace</h2>
      <p class="muted">
        Which prefixes hold the keyspace — the usual answer to "what is growing".
        ${h.namespacesPartial ? 'Partial scan: the cursor budget was reached, so these are a sample rather than the whole keyspace.' : ''}
        ${h.namespacesAt ? `Scanned ${escapeHtml(fmtDateTime(h.namespacesAt))}.` : ''}
      </p>
      <table><thead><tr><th>Namespace</th><th class="num">Keys</th><th class="num">Share</th></tr></thead>
      <tbody>${rows}</tbody></table>
    </div>`;
}

/**
 * Per-day events worth a column of their own.
 *
 * A daily row that only reported bytes would hide the things that actually
 * explain the bytes: the restart that reset every counter, the probes that
 * never came back, the moment somebody unset maxmemory. None of those survive
 * an average, so they are recorded per day by the rollup and surfaced here.
 */
function dayEvents(p: RedisHistory['points'][number]): string {
  const bits: string[] = [];
  if (p.restartCount) bits.push(`${p.restartCount} restart${p.restartCount === 1 ? '' : 's'}`);
  if (p.probeFailures) bits.push(`${fmtNumber(p.probeFailures)} failed probes`);
  if (p.maxMemoryChanged) bits.push('maxmemory changed');
  if (p.evictedKeysDelta) bits.push(`${fmtNumber(p.evictedKeysDelta)} evicted`);
  return bits.length === 0 ? '<span class="muted">—</span>' : escapeHtml(bits.join(' · '));
}

function renderHistoryTable(h: RedisHistory, page: number, pageSize: number): string {
  // Daily windows are one stored rollup row per day rather than a bucket of
  // samples, so the table gains the columns that only exist at that grain.
  const daily = isDailyRedisWindow(h.window);
  const heading = daily ? 'Daily history' : 'Recent samples';
  const unit = daily ? 'days' : 'samples';

  if (h.points.length === 0) {
    return `<div class="card"><h2>${heading}</h2><p class="muted">No ${unit} in this window yet.</p></div>`;
  }
  // Newest first — the chart is the overview, this is for reading exact numbers
  // off, so the most recent bucket is the one you want at the top.
  const newestFirst = h.points.slice().reverse();
  const start = (page - 1) * pageSize;
  const rows = newestFirst
    .slice(start, start + pageSize)
    .map((p, i) => {
      // Delta against the next-older bucket, which may sit on the previous
      // page — index into the full series, not the page slice, or the first
      // row of every page would show a dash.
      const prev = newestFirst[start + i + 1];
      const delta = prev ? p.usedMaxBytes - prev.usedMaxBytes : null;
      const state = dayStateLabel(p);
      const when = daily ? fmtDate(p.t) : fmtDateTime(p.t);
      const extra = daily
        ? `<td>${
            state === null
              ? '<span class="muted">—</span>'
              : `<span class="${toneToPillClass(state.tone)}">${escapeHtml(state.text)}</span>`
          }</td>
           <td>${p.usedMaxAt == null ? '<span class="muted">—</span>' : escapeHtml(fmtTime(p.usedMaxAt))}</td>
           <td>${dayEvents(p)}</td>`
        : '';
      return `<tr>
        <td>${escapeHtml(when)}</td>
        <td class="num">${escapeHtml(fmtBytes(p.usedMaxBytes))}</td>
        <td class="num">${escapeHtml(fmtBytes(p.datasetMaxBytes))}</td>
        <td class="num">${fmtNumber(p.keyCountMax)}</td>
        <td class="num">${delta === null ? '—' : escapeHtml(growthLabel(delta, ''))}</td>
        ${extra}
      </tr>`;
    })
    .join('');
  const extraHead = daily
    ? '<th>Worst state</th><th>Peak at</th><th>Events</th>'
    : '';
  return `
    <div class="card">
      <h2>${heading}</h2>
      ${
        daily
          ? '<p class="muted">One row per UTC day. Memory and keys are the day’s PEAK, not its average — an average would flatten exactly the spike worth reading.</p>'
          : ''
      }
      <div class="rm-table-scroll">
        <table>
          <thead><tr><th>When</th><th class="num">Memory</th><th class="num">Dataset</th><th class="num">Keys</th><th class="num">Δ</th>${extraHead}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${renderPager(page, h.points.length, pageSize, unit)}
    </div>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
